/**
 * @file enums.ts
 * @description eQMS enum/const 정의 — SSOT: docs/data-model.md
 *
 * 동적 메타모델 원칙:
 * - 13모듈 하드코딩 상태머신 제거 (워크플로우 엔진이 DB에서 관리)
 * - 여기엔 **스키마 수준**에서 필요한 enum만 남김:
 *   input_type(12종), 워크플로우 상태 분류, 관계유형, 시스템역할 등
 * - 도메인별 상태(CAPA단계, NCR상태 등)는 code_groups/codes + workflow_statuses로 관리
 */

// ---------------------------------------------------------------------------
// 필드 입력 유형 (input_type) — data-model.md §3.3
// field_defs.input_type 컬럼 값
// ---------------------------------------------------------------------------

/** 동적 필드 입력 유형 (12종 + V72 확장 table·note) */
export const InputType = {
  TEXT: "text",
  TEXTAREA: "textarea",
  DATE: "date",
  NUMBER: "number",
  PERCENT: "percent",
  SELECT: "select",
  MULTISELECT: "multiselect",
  FILE: "file",
  URL: "url",
  REF: "ref",
  WORKFLOW: "workflow",
  HTML: "html",
  /** V72: 동적 행 표(자식 컬럼 = parent_id 로 연결된 field_defs). */
  TABLE: "table",
  /** V72: 표시전용 안내문(값 없음, 조항 배너 등). */
  NOTE: "note",
  /** V72: 5-Why 인과 연쇄 위젯(값=문자열 배열, Why1..Why4 + 근본원인). QMS 공용 위젯. */
  FIVEWHY: "fivewhy",
  /** V77: 단일선택 라디오(코드그룹 참조, 값=코드 문자열). select 와 값모델 동일, 렌더만 칩(csg-chk-pill). */
  RADIO: "radio",
  /** V86: 사용자 선택(값=user id). 셀렉트 + 선택 시 아바타 렌더. 담당자·다기능팀 등. */
  USER: "user",
  /** V86: 불리언 체크박스(값=true/false). 경영검토 대상 등. */
  CHECKBOX: "checkbox",
  /** V146: 부서 선택(값=departments.id). */
  DEPT: "dept",
  /** 다른 업무유형 기록을 만들고 연결하는 버튼(V153, field_defs.config 로 대상 지정). */
  LINKCREATE: "linkcreate",
  /** 코드그룹의 코드마다 숫자 한 칸씩 받는 입력(값={코드:숫자}, 예: 평가 항목 점수·KPI 실적). config 로 그룹·범위·가중치 속성 지정. */
  CODEGRID: "codegrid",
} as const;
export type InputType = (typeof InputType)[keyof typeof InputType];

// ---------------------------------------------------------------------------
// 워크플로우 상태 분류 (classify) — data-model.md §3.4
// workflow_statuses.classify 컬럼 값
// ---------------------------------------------------------------------------

/** 워크플로우 상태 분류 */
export const StatusClassify = {
  /** 시작 상태 */
  START: "start",
  /** 진행 상태 */
  PROGRESS: "progress",
  /** 종료 상태 */
  END: "end",
} as const;
export type StatusClassify =
  (typeof StatusClassify)[keyof typeof StatusClassify];

// ---------------------------------------------------------------------------
// 아이템 관계 유형 — data-model.md §3.3 item_relations.relation_type
// ---------------------------------------------------------------------------

/** 아이템 간 관계 유형 */
export const RelationType = {
  /** 추적성 (요구사항 → 테스트 등) */
  TRACE: "trace",
  /** 부모-자식 */
  PARENT: "parent",
  /** 중복 */
  DUPLICATE: "duplicate",
  /** 차단 (선행 의존) */
  BLOCKS: "blocks",
  /** 관련 참조 */
  RELATED: "related",
} as const;
export type RelationType = (typeof RelationType)[keyof typeof RelationType];

// ---------------------------------------------------------------------------
// 폴더 종류 — data-model.md §3.3 folders.kind
// ---------------------------------------------------------------------------

/** 폴더 종류 */
export const FolderKind = {
  /** 업무 폴더 */
  WORKITEM: "workitem",
  /** 프로젝트 폴더 */
  PROJECT: "project",
  /** 문서 분류트리 폴더(DOC 모듈 택소노미) */
  DOC: "doc",
} as const;
export type FolderKind = (typeof FolderKind)[keyof typeof FolderKind];

// ---------------------------------------------------------------------------
// 시스템 역할 — data-model.md §3.1 roles
// ---------------------------------------------------------------------------

/** 기본 역할 코드 (RBAC — roles.code). 권한 판정 기준이며 표시 이름(roles.name)과 별개. 서버 RoleCodes 와 일치. */
export const SystemRole = {
  /** 공급사 슈퍼관리자 */
  SUPER_ADMIN: "SUPER_ADMIN",
  /** 회사 관리자 (사이트관리자) */
  COMPANY_ADMIN: "COMPANY_ADMIN",
  /** 품질책임자 */
  QUALITY_LEAD: "QUALITY_LEAD",
  /** 일반 사용자 */
  MEMBER: "MEMBER",
  /** 열람 전용 */
  VIEWER: "VIEWER",
} as const;
export type SystemRole = (typeof SystemRole)[keyof typeof SystemRole];

// ---------------------------------------------------------------------------
// 전자서명 의미 — data-model.md §3.7, system-codes-seed.md §J
// ---------------------------------------------------------------------------

/** 전자서명 의미 (Part 11) */
export const SignatureMeaning = {
  /** 작성 책임 */
  AUTHORED: "Authored",
  /** 검토 완료 */
  REVIEWED: "Reviewed",
  /** 승인 및 발효 */
  APPROVED: "Approved",
} as const;
export type SignatureMeaning =
  (typeof SignatureMeaning)[keyof typeof SignatureMeaning];

// ---------------------------------------------------------------------------
// 모듈 코드 (13 고정) — data-model.md §3.2, system-codes-seed.md §A
// 프로비저닝 축. 하드코딩 엔티티 아님 — 모듈 on/off 식별용
// ---------------------------------------------------------------------------

/** 13 모듈 코드 (프로비저닝용) */
export const ModuleCode = {
  DOCUMENTS: "documents",
  COMPLAINT: "complaint",
  CAPA: "capa",
  NC: "nc",
  CHANGE: "change",
  RISK: "risk",
  AUDIT: "audit",
  TRAINING: "training",
  DESIGN: "design",
  ALM: "alm",
  TRACE: "trace",
  CSV: "csv",
} as const;
export type ModuleCode = (typeof ModuleCode)[keyof typeof ModuleCode];

// ---------------------------------------------------------------------------
// 심각도 체계 — data-model.md §2, system-codes-seed.md §E 주석
// 코드그룹으로 관리되지만 TS 측에서 참조용 상수 제공
// ---------------------------------------------------------------------------

/** 공통 심각도 (위험/CAPA/변경/공급자) */
export const SeverityLevel = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const;
export type SeverityLevel = (typeof SeverityLevel)[keyof typeof SeverityLevel];

/** NCR/결함 심각도 */
export const SeverityNcr = {
  CRITICAL: "critical",
  MAJOR: "major",
  MINOR: "minor",
} as const;
export type SeverityNcr = (typeof SeverityNcr)[keyof typeof SeverityNcr];

// ---------------------------------------------------------------------------
// 게이트 판정 — system-codes-seed.md §I
// ---------------------------------------------------------------------------

/** 프로젝트 게이트 판정 */
export const GateDecision = {
  /** 통과(승인) */
  PASS: "pass",
  /** 조건부 승인 */
  CONDITIONAL: "cond",
  /** 보류 */
  HOLD: "hold",
} as const;
export type GateDecision = (typeof GateDecision)[keyof typeof GateDecision];

// ---------------------------------------------------------------------------
// 알림 심각도 — system-codes-seed.md §J
// ---------------------------------------------------------------------------

/** 알림 심각도 */
export const NotificationSeverity = {
  CRITICAL: "crit",
  WARNING: "warn",
  INFO: "info",
} as const;
export type NotificationSeverity =
  (typeof NotificationSeverity)[keyof typeof NotificationSeverity];
